import { useTranslation } from 'react-i18next';
import { Link, useLocation } from '@tanstack/react-router';
import { useAuth } from '../../../hooks/useAuth.js';

interface SidebarProps {
  /** モバイルでの表示状態 */
  isOpen?: boolean;
  /** 閉じる処理 */
  onClose?: () => void;
}

interface NavItem {
  key: string;
  path: string;
  icon: React.ReactNode;
}

/** @graph-connects none */
const pipelinesNavItem: NavItem = {
  key: 'pipelines',
  path: '/pipelines',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  ),
};

/**
 * SNS コンテンツ生成ナビアイテム（cortex_admin_sns_content_generator_permissions.allowedEmails/Groups 限定で表示）
 * @graph-connects none
 */
const snsContentGeneratorNavItem: NavItem = {
  key: 'snsContentGenerator',
  path: '/sns-content-generator',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.456-2.456L14.25 6l1.035-.259a3.375 3.375 0 002.456-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const settingsNavItem: NavItem = {
  key: 'settings',
  path: '/settings',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const dataViewerRegisterNavItem: NavItem = {
  key: 'dataViewerRegister',
  path: '/data-viewer-register',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
};

/**
 * pipeline 管理権限ナビアイテム（pipeline_permissions.admins 限定で表示）
 * @graph-connects none
 */
const pipelinePermissionsNavItem: NavItem = {
  key: 'pipelinePermissions',
  path: '/pipeline-permissions',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 11c0-1.105.895-2 2-2s2 .895 2 2v3h2v-3a4 4 0 10-8 0v3h2v-3zM5 12a2 2 0 012-2h10a2 2 0 012 2v7a2 2 0 01-2 2H7a2 2 0 01-2-2v-7z"
      />
    </svg>
  ),
};

/**
 * Workspace 権限ナビアイテム（cortex_admin_workspace_permissions の管理者限定で表示）
 * @graph-connects none
 */
const workspacePermissionsNavItem: NavItem = {
  key: 'workspacePermissions',
  path: '/workspace-permissions',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  ),
};

/**
 * 退職者 cortex リソース移譲ナビアイテム（pipeline_permissions.admins 限定で表示）
 * @graph-connects none
 */
const ownershipTransfersNavItem: NavItem = {
  key: 'ownershipTransfers',
  path: '/ownership-transfers',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 014-4h1m0-4a4 4 0 100-8 4 4 0 000 8zm6 4l4 4m0-4l-4 4"
      />
    </svg>
  ),
};

/**
 * クエリ実行ナビアイテム（cortex_admin_data_viewer_permissions.allowedEmails 限定で表示）
 * @graph-connects none
 */
const dataViewerNavItem: NavItem = {
  key: 'dataViewer',
  path: '/data-viewer',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  ),
};

/**
 * bot 稼働状況ナビアイテム（認証済み社員全員に表示）
 *
 * 母艦で動く bot が止まったことに誰も気付かない状態を解消するためのページなので、
 * 権限で絞らない。見える人が多いほど気付く確率が上がる。
 * @graph-connects none
 */
const botStatusNavItem: NavItem = {
  key: 'botStatus',
  path: '/bot-status',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  ),
};

/**
 * Claude 利用状況ナビアイテム（認証済み社員全員に表示）
 *
 * bot 稼働状況と同じく権限で絞らない。こちらは「動いているか」ではなく「何にどれだけ
 * 使ったか」を見るページで、bot-status の隣に置く。
 * @graph-connects none
 */
const claudeUsageNavItem: NavItem = {
  key: 'claudeUsage',
  path: '/claude-usage',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  ),
};

/**
 * インフラ構成図ナビアイテム（認証済み社員全員に表示）
 * @graph-connects none
 */
const infraGraphNavItem: NavItem = {
  key: 'infraGraph',
  path: '/infra-graph',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 8a2 2 0 100-4 2 2 0 000 4zM5 8v8m0 0a2 2 0 100 4 2 2 0 000-4zm14-8a2 2 0 100-4 2 2 0 000 4zm0 0v3a2 2 0 01-2 2h-4m-3 7a2 2 0 100-4 2 2 0 000 4z"
      />
    </svg>
  ),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business サイドバーナビゲーション。Pipelines・Settingsへのリンクに加え、pipeline_permissions.admins（user.isPipelineAdmin）の場合のみ権限管理リンクを表示。ガラスカード背景で提供し、モバイルスライドイン・デスクトップ固定の2モード対応
 * @graph-connects none
 */
export function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();

  const isActive = (path: string) => location.pathname.startsWith(path);

  // pipeline_permissions.admins だけに権限管理リンクを見せる
  // cortex_admin_data_viewer_permissions.allowedEmails だけにクエリ実行リンクを見せる
  // cortex_admin_sns_content_generator_permissions.allowedEmails/Groups だけに SNS コンテンツ生成リンクを見せる
  const navItems: NavItem[] = [
    pipelinesNavItem,
    ...(user?.canUseSnsContentGenerator ? [snsContentGeneratorNavItem] : []),
    ...(user?.isPipelineAdmin ? [pipelinePermissionsNavItem, ownershipTransfersNavItem] : []),
    ...(user?.isWorkspacePermissionAdmin ? [workspacePermissionsNavItem] : []),
    dataViewerNavItem,
    ...(user?.canRegisterQuery ? [dataViewerRegisterNavItem] : []),
    infraGraphNavItem,
    botStatusNavItem,
    claudeUsageNavItem,
    settingsNavItem,
  ];

  const sidebarContent = (
    <nav className="flex flex-col h-full" aria-label="Main navigation">
      {/* ナビゲーションアイテム */}
      <div className="flex-1 py-4 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.key}
            to={item.path}
            onClick={onClose}
            className={`
              flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg
              text-sm font-medium transition-colors
              ${
                isActive(item.path)
                  ? 'bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)]'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
              }
            `}
            aria-current={isActive(item.path) ? 'page' : undefined}
          >
            {item.icon}
            <span>{t(`nav.${item.key}`)}</span>
          </Link>
        ))}
      </div>

      {/* フッター */}
      <div className="p-4 border-t border-[var(--color-border)]">
        <p className="text-xs text-[var(--color-text-muted)] text-center">Cortex Admin</p>
      </div>
    </nav>
  );

  return (
    <>
      {/* モバイルオーバーレイ */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* モバイルサイドバー（スライドイン） */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-[var(--color-bg-surface)] border-r border-[var(--color-border)]
          transform transition-transform duration-300 ease-in-out
          md:hidden
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* モバイルヘッダー */}
        <div className="flex items-center justify-between h-14 px-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[var(--color-accent-primary)] flex items-center justify-center">
              <span className="text-white font-bold text-sm">CA</span>
            </div>
            <span className="font-semibold gradient-text">{t('app.name')}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label={t('common.close')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        {sidebarContent}
      </aside>

      {/* デスクトップサイドバー（ガラスカード背景） */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:flex-shrink-0 glass-card border-r border-[var(--glass-border)] rounded-none">
        {sidebarContent}
      </aside>
    </>
  );
}
